import React from "react";
import { Alert, Button, Result } from "antd";
import { FiArrowLeft } from "react-icons/fi";
import { useHistory, useLocation } from "react-router-dom";
import { formatCurrency, parseApiAmount } from "../../utils/amountFormat";
import {
  influencerPaymentStatusClass,
  influencerPaymentStatusLabel,
} from "../../utils/influencerPaymentStatus";
import parsePhoneNumberFromString from "libphonenumber-js/min";

export const AgencyPaymentHistoryDetail = () => {
  const history = useHistory();
  const { state: record } = useLocation();

  if (!record?.pay_report_id || !record?.id) {
    return (
      <div className="mx-auto max-w-[640px]">
        <Result
          status="warning"
          title="정산 내역을 찾을 수 없습니다."
          subTitle="목록에서 다시 선택하거나 전체 정산 내역을 불러온 뒤 시도해 주세요."
          extra={
            <Button
              type="primary"
              onClick={() =>
                history.replace("/payments/agency-payments-history")
              }
            >
              정산 목록으로
            </Button>
          }
        />
      </div>
    );
  }

  const currency = record.currency ?? "KRW";
  const settlement = parseApiAmount(record.settlement_amount);
  const tax = parseApiAmount(record.tax_amount);
  const net = parseApiAmount(record.net_amount);
  const range =
    record.campaign_start_at && record.campaign_end_at
      ? `${record.campaign_start_at.split(" ")[0]} ~ ${
          record.campaign_end_at.split(" ")[0]
        }`
      : null;

  const amountRows = [
    {
      label: "리워드",
      value: formatCurrency(settlement, currency),
    },
    { label: "원천징수", value: formatCurrency(tax, currency) },
    { label: "실지급액", value: formatCurrency(net, currency) },
  ];

  return (
    <div className="w-full max-w-[1400px]">
      <h2 className="text-3xl font-bold tracking-[-0.03em] text-[#111111] md:text-4xl">
        정산 내역 상세
      </h2>

      <Button
        type="button"
        onClick={() => history.goBack()}
        className="mb-5 text-lg md:text-xl inline-flex items-center gap-2 border border-[#e1e6ec] bg-white px-4 py-2 font-semibold text-[#343a40]"
      >
        <FiArrowLeft className="text-lg md:text-xl" />
        목록으로
      </Button>

      <div className="mb-5 mt-5">
        <Alert
          type="info"
          showIcon
          description={
            <div className="text-[15px]">
              <p>
                에이전시에 소속되어 있는 크리에이터의 경우, 에이전시 통장으로
                정산됩니다. 아래 크리에이터 정보를 확인하시고 소속
                크리에이터에게 따로 지급해주세요.
              </p>
            </div>
          }
          className="rounded-2xl border-[#bfdbfe] bg-[#eff6ff] text-[14px]"
        />
      </div>

      <section className="mb-6 overflow-hidden rounded-[22px] border border-[#e7eaee] bg-white shadow-[0_14px_36px_rgba(17,24,39,0.05)]">
        <div className="flex flex-col gap-5 p-5 md:flex-row md:items-start md:gap-6 md:p-6">
          <div className="min-w-0 flex-1">
            <span
              className={`inline-flex rounded-[8px] px-2.5 py-1 text-[12px] font-bold ${influencerPaymentStatusClass(
                record.status
              )}`}
            >
              {influencerPaymentStatusLabel(record.status)}
            </span>
            <h3 className="mt-3 break-keep text-[20px] font-bold leading-snug tracking-[-0.03em] text-[#111827] md:text-[24px]">
              {record.nickname}
            </h3>
            <p className="mt-2 text-[13px] leading-snug text-[#64748b]">
              <span className="break-keep font-semibold text-[#475569]">
                {record.campaign_title ?? "—"}
              </span>
            </p>
            {range ? (
              <p className="mt-1.5 break-keep text-[13px] text-[#94a3b8]">
                {range}
              </p>
            ) : null}
            {record.paid_at ? (
              <p className="mt-1.5 break-keep text-[13px] text-[#94a3b8]">
                지급일 {record.paid_at}
              </p>
            ) : null}
          </div>
        </div>

        <div className="border-t border-[#f1f5f9] bg-[#0f172a] px-5 py-4 text-white md:px-6">
          <p className="text-[12px] font-medium text-white/80">
            크리에이터 실지급액
          </p>
          <p className="mt-1 text-right text-[22px] font-bold tracking-[-0.04em] md:text-[26px]">
            {formatCurrency(net, currency)}
          </p>
        </div>
      </section>

      <section className="mb-6 rounded-[18px] border border-[#e7eaee] bg-white p-5 shadow-[0_10px_28px_rgba(17,24,39,0.04)] md:p-6">
        <h4 className="text-[16px] font-bold text-[#111827]">금액 상세</h4>
        <dl className="mt-4 space-y-3">
          {amountRows.map((row) => (
            <div
              key={row.label}
              className="flex items-baseline justify-between gap-4 border-b border-[#f1f5f9] pb-3 last:border-0 last:pb-0"
            >
              <dt className="text-[13px] font-medium text-[#64748b]">
                {row.label}
              </dt>
              <dd className="text-[15px] font-bold tabular-nums text-[#111827]">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mb-6 rounded-[18px] border border-[#e7eaee] bg-white p-5 md:p-6">
        <h4 className="text-[16px] font-bold text-[#111827]">
          크리에이터 정보
        </h4>
        <div className="mt-3 space-y-2 text-[14px] bg-[#f8fafc] p-3 rounded-xl">
          <p className="leading-relaxed">
            <span className="text-[13px] font-medium text-[#64748b]">
              닉네임
            </span>{" "}
            <span className="font-semibold text-[#111827]">
              {record.nickname}
            </span>
          </p>
          {record.email ? (
            <p className="leading-relaxed break-words">
              <span className="text-[13px] font-medium text-[#64748b]">
                이메일
              </span>{" "}
              <span className="font-semibold text-[#111827]">
                {record.email}
              </span>
            </p>
          ) : null}
          {record.phone ? (
            <p className="leading-relaxed">
              <span className="text-[13px] font-medium text-[#64748b]">
                연락처
              </span>{" "}
              <span className="font-semibold text-[#111827]">
                {parsePhoneNumberFromString(record.phone).formatNational()}
              </span>
            </p>
          ) : null}
          <p className="leading-relaxed">
            <span className="text-[13px] font-medium text-[#64748b]">
              은행명
            </span>{" "}
            <span className="font-semibold text-[#111827]">
              {record.bank_name ?? "-"}
            </span>
          </p>
          <p className="leading-relaxed break-all">
            <span className="text-[13px] font-medium text-[#64748b]">
              계좌번호
            </span>{" "}
            <span className="font-semibold text-[#111827]">
              {record.bank_account_number ?? "-"}
            </span>
          </p>
          <div>
            <span className="block text-[13px] font-medium text-[#64748b]">
              통장 사본
            </span>
            <div className="mt-1">
              {record.bank_account_url ? (
                <img
                  src={record.bank_account_url}
                  alt="통장 사본"
                  className="h-auto max-h-[150px] object-contain cursor-pointer rounded-xl"
                  onClick={() => window.open(record.bank_account_url, "_blank")}
                />
              ) : (
                <span className="">등록된 이미지 없음</span>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
